import React from 'react';
import { Switcher } from '../../components/Form/Switcher/Switcher';
import { addCategory, patchCategory, CategoryEdit, Category } from '../../model/category';
import { t } from '../../locales/translate';
import { Label } from '../../components/Form/Label/Label';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { Icon } from '../../components/Icon/Icon';
import sortOrderExample from './../../assets/sort_order.jpg';
import sortOrderHomepageExample from './../../assets/sort_order_homepage.jpg';
import sortOrderTopLeft from './../../assets/sort_order_top_left.jpg';
import sortOrderTopRight from './../../assets/sort_order_top_right.jpg';
import sortOrderBottom from './../../assets/sort_order_bottom.jpg';
import { Stack } from '../../components/Stack/Stack';
import { Spacer } from '../../components/Spacer/Spacer';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { FormForm } from '../../components/Form/FormForm';
import { FormTextArea } from '../../components/Form/TextArea/FormTextArea';
import { FormTextField } from '../../components/Form/TextField/FormTextField';
import { handleError } from '../../model/notification';
import { FormSearch } from '../../components/Form/AsyncSearch/FormSearch';
import { Grid } from '../../components/Grid/Grid';
import { FormSelect } from '../../components/Form/Select/FormSelect';
import { FormNumberField } from '../../components/Form/NumberField/FormNumberField';
import { FormImageUpload } from '../../components/Form/FileUpload/FormImageUpload';
import { SLUG_REGEX } from '../../model/regex';

interface Props {
  model: Partial<CategoryEdit>;
  onClose: () => void;
}

export function CategoryForm({ model: propsModel, onClose }: Props): JSX.Element {
  const newCategory = !propsModel.id;
  const { model, fields, valid, dirty, submissionStatus, onSubmit, changes } = useForm<Partial<CategoryEdit>>({
    model: {
      ...propsModel,
      type: propsModel.type || 'category',
    },
    onSubmit: async () => {
      if (newCategory) {
        await addCategory(model);
      } else {
        await patchCategory({ ...changes, id: model.id });
      }
      onClose();
    },
    onSubmitError: handleError,
    validations: {
      shopId: 'required',
      nameDe: 'required',
      slug: !newCategory ? ['required', SLUG_REGEX] : SLUG_REGEX,
    },
  });

  return (
    <FormForm
      onSubmit={onSubmit}
      canSubmit={valid && dirty}
      loading={submissionStatus === 'submitting'}
      onClose={onClose}
    >
      <Stack>
        {model.id && (
          <FormTextField
            {...fields.slug}
            inputId="slug"
            label={t.model.category.slug}
            required={!newCategory}
            description={t.model.category.slugFormat}
          />
        )}
        <Grid>
          <FormTextField inputId="nameDe" label={`${t.general.name} ${t.general.german}`} {...fields.nameDe} />
          <FormTextField label={`${t.general.name} ${t.general.english}`} {...fields.nameEn} />
        </Grid>
        <FormTextArea label={t.general.description} {...fields.description} />
        <FormTextField
          inputId="alternativeNames"
          label={t.model.category.altNames}
          description={t.model.category.altNamesDesc}
          {...fields.alternativeNames}
        />
        {newCategory && (
          <FormSearch
            inputId="shopId"
            label={t.model.shop._name}
            disabled={!!fields.parentCategoryId.value}
            description={
              fields.parentCategoryId.value
                ? `* ${t.model.category.shopFromParentCategory}`
                : !newCategory
                  ? `* ${t.general.cantBeEdited}`
                  : ''
            }
            maxResults={25}
            searchIn={['shop']}
            {...fields.shopId}
          />
        )}
        {newCategory && (
          <FormSearch
            inputId="parentCategoryId"
            label={t.model.shop.parent}
            disabled={!newCategory}
            description={!newCategory ? `* ${t.general.cantBeEdited}` : ''}
            searchIn={['category']}
            additionalParams={{ shopId: model.shopId || null }}
            onChangeObject={(value) => fields.shopId.onChange((value as Category | null)?.shop?.id)}
            {...fields.parentCategoryId}
          />
        )}
        <Switcher
          label={t.model.category.mainCategory}
          value={fields.mainCategory.value}
          onChange={(v) => fields.mainCategory.onChange(v)}
          labels={[t.general.yes, t.general.no]}
        />
        <FormSelect
          name="productType"
          {...fields.type}
          value={fields.type.value}
          options={[
            { value: 'category', label: 'Category' },
            { value: 'artist', label: 'Artist' },
            { value: 'brand', label: 'Brand' },
            { value: 'label', label: 'Label' },
          ]}
          label={t.general.type}
          required
          disabled={!newCategory}
          description={!newCategory ? `* ${t.general.cantBeEdited}` : ''}
        />
        <Spacer size="none" />
        <Label title={t.model.image._plural}></Label>
        <FormImageUpload label={t.model.image.square} {...fields.squareImage} />
        <FormImageUpload label={t.model.image.portrait} {...fields.portraitImage} />
        <FormImageUpload label={t.model.image.landscape} {...fields.landscapeImage} />
        <Spacer size="none" />
        <Label title={t.model.tour.sortOrder}></Label>
        <Stack className="category-form__sort-order">
          <FormNumberField
            {...fields.order}
            label={t.model.tour.sortOrder}
            actions={
              <Tooltip position="left" reference={<Icon name="question--sm" />}>
                <div className="category-form__example-tooltip">
                  <p>{t.model.category.orderTooltip}</p>
                  <strong>{t.general.example}</strong>
                  <img className="category-form__sort-order-example" src={sortOrderExample} />
                </div>
              </Tooltip>
            }
          />
          <FormNumberField
            {...fields.sortOrderHomepage}
            label={t.model.tour.sortOrderHomepage}
            actions={
              <Tooltip position="left" reference={<Icon name="question--sm" />}>
                <div className="category-form__example-tooltip">
                  <p>{t.model.category.orderHomepageTooltip}</p>
                  <strong>{t.general.example}</strong>
                  <img className="category-form__sort-order-homepage-example" src={sortOrderHomepageExample} />
                </div>
              </Tooltip>
            }
          />
          <FormNumberField
            {...fields.sortOrderTicketTopLeft}
            label={t.model.category.sortOrderFeatureTopLeft}
            actions={
              <Tooltip position="top-end" reference={<Icon name="question--sm" />}>
                <div className="category-form__example-tooltip">
                  <p>{t.model.category.orderTicketTopLeftTooltip}</p>
                  <strong>{t.general.example}</strong>
                  <img className="category-form__sort-order-ticket-top-left-example" src={sortOrderTopLeft} />
                </div>
              </Tooltip>
            }
          />
          <FormNumberField
            {...fields.sortOrderTicketTopRight}
            label={t.model.category.sortOrderFeatureTopRight}
            actions={
              <Tooltip position="top-end" reference={<Icon name="question--sm" />}>
                <div className="category-form__example-tooltip">
                  <p>{t.model.category.orderTicketTopRightTooltip}</p>
                  <strong>{t.general.example}</strong>
                  <img className="category-form__sort-order-ticket-top-right-example" src={sortOrderTopRight} />
                </div>
              </Tooltip>
            }
          />
          <FormNumberField
            {...fields.sortOrderTicketBottom}
            label={t.model.category.sortOrderFeatureBottom}
            actions={
              <Tooltip position="top-end" reference={<Icon name="question--sm" />}>
                <div className="category-form__example-tooltip">
                  <p>{t.model.category.orderTicketBottomTooltip}</p>
                  <strong>{t.general.example}</strong>
                  <img className="category-form__sort-order-ticket-bottom-example" src={sortOrderBottom} />
                </div>
              </Tooltip>
            }
          />
        </Stack>
      </Stack>
    </FormForm>
  );
}
